const CAROUSEL_SELECTOR = '.niccos-isstories__carousel';
const PREV_SELECTOR = '.niccos-isstories__nav--prev';
const NEXT_SELECTOR = '.niccos-isstories__nav--next';

class NiccosIsstoriesList extends HTMLElement {
  constructor() {
    super();
    this._flkty = null;
  }

  connectedCallback() {
    this._onReady = this._onReady.bind(this);

    if (typeof Flickity !== 'undefined') {
      this._init();
    } else {
      document.addEventListener('page:loaded', () => this._init(), { once: true });
    }
  }

  disconnectedCallback() {
    this._destroy();
  }

  _init() {
    this._carouselEl = this.querySelector(CAROUSEL_SELECTOR);
    if (!this._carouselEl) return;

    this._prevBtn = this.querySelector(PREV_SELECTOR);
    this._nextBtn = this.querySelector(NEXT_SELECTOR);

    this._destroy();

    this._carouselEl.querySelectorAll('.niccos-isstories__cell').forEach(cell => {
      if (!cell.querySelector('.niccos-isstories__bubble')) cell.remove();
    });

    this._buildCarousel();

    this._resizeTimer = null;
    this._onResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this._buildCarousel(), 150);
    };
    window.addEventListener('resize', this._onResize);
  }

  _needsSlider() {
    const el = this._carouselEl;
    if (!el) return false;

    const cells = el.querySelectorAll('.niccos-isstories__cell');
    if (cells.length === 0) return false;

    const gap = parseFloat(getComputedStyle(el).columnGap)
      || parseFloat(getComputedStyle(el).gap) || 0;

    let totalWidth = 0;
    cells.forEach((cell, i) => {
      totalWidth += cell.offsetWidth;
      if (i < cells.length - 1) {
        totalWidth += gap || parseFloat(getComputedStyle(cell).marginRight) || 0;
      }
    });

    return totalWidth > el.offsetWidth;
  }

  _buildCarousel() {
    const hadSlider = !!this._flkty;
    if (hadSlider) this._destroyFlickity();

    const needsSlider = this._needsSlider();

    if (needsSlider) {
      this._initFlickity();
    }

    this._toggleNav(needsSlider);
  }

  _initFlickity() {
    const el = this._carouselEl;

    this._flkty = new Flickity(el, {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      freeScroll: false,
      wrapAround: false,
      groupCells: 1,
      rightToLeft: document.documentElement.getAttribute('dir') === 'rtl',
      on: { ready: this._onReady }
    });

    this._flkty.on('select', () => this._syncNav());
    this._flkty.on('settle', () => this._syncNav());

    if (this._prevBtn) {
      this._prevBtn.addEventListener('click', this._onPrev = (e) => {
        e.preventDefault();
        this._flkty.previous();
      });
    }

    if (this._nextBtn) {
      this._nextBtn.addEventListener('click', this._onNext = (e) => {
        e.preventDefault();
        this._flkty.next();
      });
    }
  }

  _toggleNav(show) {
    if (this._prevBtn) this._prevBtn.style.display = show ? '' : 'none';
    if (this._nextBtn) this._nextBtn.style.display = show ? '' : 'none';
  }

  _onReady() {
    this._syncNav();
  }

  _syncNav() {
    if (!this._flkty) return;
    const slides = this._flkty.slides;
    if (!slides.length) return;

    const idx = this._flkty.selectedIndex;
    const last = slides.length - 1;
    const curTarget = slides[idx].target;

    const atStart = idx === 0 || Math.abs(slides[idx - 1].target - curTarget) < 1;
    const atEnd   = idx === last || Math.abs(slides[idx + 1].target - curTarget) < 1;

    if (this._prevBtn) this._prevBtn.disabled = atStart;
    if (this._nextBtn) this._nextBtn.disabled = atEnd;
  }

  _destroyFlickity() {
    if (this._flkty) {
      this._flkty.destroy();
      this._flkty = null;
    }
    if (this._prevBtn && this._onPrev) {
      this._prevBtn.removeEventListener('click', this._onPrev);
      this._onPrev = null;
    }
    if (this._nextBtn && this._onNext) {
      this._nextBtn.removeEventListener('click', this._onNext);
      this._onNext = null;
    }
  }

  _destroy() {
    this._destroyFlickity();
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
      this._onResize = null;
    }
    clearTimeout(this._resizeTimer);
  }
}

if (!customElements.get('niccos-isstories-list')) {
  customElements.define('niccos-isstories-list', NiccosIsstoriesList);
}



class NiccosIsstoriesPopup extends HTMLElement {
  constructor() {
    super();
    this._slides = [];
    this._currentIndex = -1;
    this._isMobile = false;
    this._touchStartX = 0;
    this._touchDeltaX = 0;
    this._built = false;

    this._currentFrameIndex = 0;
    this._barAnimationId = null;
    this._barStartTime = 0;
    this._barDuration = 0;
    this._barPausedElapsed = 0;
    this._isPaused = false;
    this._holdPaused = false;
    this._isMuted = true;
    this._videoEndedHandler = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._sectionEl = this.closest('[id^="shopify-section-"]');

    if (this._sectionEl && this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    this._overlay = this.querySelector('.niccos-isstories-popup__overlay');
    this._container = this.querySelector('.niccos-isstories-popup__container');
    this._prevBtn = this.querySelector('.niccos-isstories-popup__arrow--prev');
    this._nextBtn = this.querySelector('.niccos-isstories-popup__arrow--next');
    this._slider = this.querySelector('.niccos-isstories-popup__slider');

    this._frameDuration = parseFloat(this.dataset.frameDuration) || 5;
    this._videoUseOwnDuration = this.dataset.videoUseOwnDuration === 'true';

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);

    this._mql = window.matchMedia('(max-width: 767px)');
    this._isMobile = this._mql.matches;
    this._mql.addEventListener('change', (e) => {
      this._isMobile = e.matches;
      if (this._currentIndex >= 0) this._updateSlides();
    });

    if (this._container) {
      this._container.addEventListener('click', (e) => {
        if (!this._isPopupContentClick(e.target)) {
          this.close();
        }
      });
    }

    if (this._overlay) this._overlay.addEventListener('click', () => this.close());
    if (this._prevBtn) this._prevBtn.addEventListener('click', () => this.prevStory());
    if (this._nextBtn) this._nextBtn.addEventListener('click', () => this.nextStory());

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-niccos-isstories-close]')) this.close();
    });

    this._initBubbleClicks();
  }

  _initBubbleClicks() {
    if (!this._sectionEl) return;

    this._sectionEl.addEventListener('click', (e) => {
      const bubble = e.target.closest('[data-niccos-isstories-open]');
      if (!bubble) return;

      // tracking start
      const titleEl = bubble.querySelector('.niccos-isstories__title p');
      const storyName = (titleEl ? titleEl.innerText : bubble.innerText).trim().toLowerCase();

      if (Shopify.analytics) {
        Shopify.analytics.publish('click_content', {
          event_label: 'homepage_story_' + storyName + '_click'
        });
      }

      // tracking end

      const cell = bubble.closest('.niccos-isstories__cell');
      if (!cell) return;

      const storyId = cell.getAttribute('data-story-id');
      if (!storyId) return;

      this._buildSlides();

      const idx = this._slides.findIndex(
        (s) => s.getAttribute('data-story-id') === storyId
      );
      this.open(idx >= 0 ? idx : 0);
    });
  }

  _buildSlides() {
    if (this._built) return;
    if (!this._sectionEl) return;

    const cells = this._sectionEl.querySelectorAll('.niccos-isstories__cell');
    cells.forEach((cell) => {
      const tpl = cell.querySelector('.niccos-isstories__popup-slide-tpl');
      if (!tpl) return;

      const clone = tpl.content.cloneNode(true);
      this._slider.appendChild(clone);
    });

    this._slides = Array.from(
      this._slider.querySelectorAll('.niccos-isstories-popup__slide')
    );

    this._slides.forEach((slide) => {
      this._bindSlideControls(slide);
      this._bindSlideTap(slide);
    });

    this._built = true;
  }

  _bindSlideControls(slide) {
    const playBtn = slide.querySelector('.niccos-isstories-popup__btn-play');
    const muteBtn = slide.querySelector('.niccos-isstories-popup__btn-mute');

    if (playBtn) {
      playBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._onPlayPauseClick(slide);
      });
    }

    if (muteBtn) {
      muteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._onMuteClick(slide);
      });
    }
  }

  _bindSlideTap(slide) {
    const media = slide.querySelector('.niccos-isstories-popup__slide-media');
    if (!media) return;

    const isControl = (target) =>
      target.closest('.niccos-isstories-popup__controls') ||
      target.closest('.niccos-isstories-popup__btn-play') ||
      target.closest('.niccos-isstories-popup__btn-mute') ||
      target.closest('.niccos-isstories-popup__frame-btn') ||
      target.closest('[data-niccos-isstories-close]');

    let holdTimer = null;
    let holdTriggered = false;
    let skipNextClick = false;

    media.addEventListener('pointerdown', (e) => {
      if (isControl(e.target)) return;
      if (!slide.classList.contains('niccos-isstories-popup__slide--active')) return;

      holdTriggered = false;
      holdTimer = setTimeout(() => {
        holdTriggered = true;
        if (!this._isPaused) {
          this._holdPaused = true;
          this._pause();
        }
      }, 200);
    });

    const onRelease = () => {
      clearTimeout(holdTimer);
      holdTimer = null;
      if (holdTriggered) {
        holdTriggered = false;
        skipNextClick = true;
        if (this._holdPaused) {
          this._holdPaused = false;
          this._resume();
        }
      }
    };

    media.addEventListener('pointerup', onRelease);
    media.addEventListener('pointercancel', onRelease);

    media.addEventListener('click', (e) => {
      if (skipNextClick) { skipNextClick = false; return; }
      if (isControl(e.target)) return;
      if (!slide.classList.contains('niccos-isstories-popup__slide--active')) return;

      e.stopPropagation();

      const rect = slide.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const half = rect.width / 2;

      if (x < half) {
        this._prevFrame();
      } else {
        this._nextFrame();
      }
    });
  }



  open(index) {
    if (!this._slides.length) return;
    this._currentIndex = index;
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onKeyDown);
    this._slider.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this._slider.addEventListener('touchmove', this._onTouchMove, { passive: false });
    this._slider.addEventListener('touchend', this._onTouchEnd);
    this._updateSlides();
    this._initFrameEngine();
  }

  close() {
    this._stopFrameEngine();
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._onKeyDown);
    this._slider.removeEventListener('touchstart', this._onTouchStart);
    this._slider.removeEventListener('touchmove', this._onTouchMove);
    this._slider.removeEventListener('touchend', this._onTouchEnd);
    this._currentIndex = -1;
  }

  prevStory() {
    if (this._currentIndex > 0) {
      this._stopFrameEngine();
      this._currentIndex -= 1;
      this._updateSlides();
      this._initFrameEngine();
    }
  }

  nextStory() {
    if (this._currentIndex < this._slides.length - 1) {
      this._stopFrameEngine();
      this._currentIndex += 1;
      this._updateSlides();
      this._initFrameEngine();
    } else {
      this.close();
    }
  }

  goTo(index) {
    if (index < 0 || index >= this._slides.length) return;
    this._stopFrameEngine();
    this._currentIndex = index;
    this._updateSlides();
    this._initFrameEngine();
  }

  _updateSlides() {
    const cur = this._currentIndex;
    const total = this._slides.length;

    this._slides.forEach((slide, i) => {
      slide.classList.remove(
        'niccos-isstories-popup__slide--active',
        'niccos-isstories-popup__slide--prev',
        'niccos-isstories-popup__slide--next'
      );

      if (i === cur) {
        slide.classList.add('niccos-isstories-popup__slide--active');
      } else if (i === cur - 1 && !this._isMobile) {
        slide.classList.add('niccos-isstories-popup__slide--prev');
      } else if (i === cur + 1 && !this._isMobile) {
        slide.classList.add('niccos-isstories-popup__slide--next');
      }
    });

    if (this._prevBtn) this._prevBtn.disabled = cur === 0;
    if (this._nextBtn) this._nextBtn.disabled = cur >= total - 1;

    this._bindNeighborClicks();
  }

  _bindNeighborClicks() {
    this._slides.forEach((slide) => {
      slide._neighborClick = null;
    });

    const cur = this._currentIndex;
    if (cur > 0 && this._slides[cur - 1]) {
      this._slides[cur - 1].onclick = () => this.prevStory();
    }
    if (cur < this._slides.length - 1 && this._slides[cur + 1]) {
      this._slides[cur + 1].onclick = () => this.nextStory();
    }
  }


  _getActiveSlide() {
    return this._slides[this._currentIndex] || null;
  }

  _getFrames(slide) {
    if (!slide) return [];
    return Array.from(slide.querySelectorAll('.niccos-isstories-popup__frame'));
  }

  _getBars(slide) {
    if (!slide) return [];
    return Array.from(slide.querySelectorAll('.niccos-isstories-popup__bar'));
  }

  _getFrameType(frame) {
    const inner = frame.querySelector('[data-frame-type]');
    return inner ? inner.dataset.frameType : 'image';
  }

  _getFrameVideo(frame) {
    return frame.querySelector('.niccos-isstories-popup__frame-video');
  }

  _getFrameSlideNameColor(frame) {
    const inner = frame.querySelector('[data-slide-name-color]');
    return inner && inner.dataset.slideNameColor ? inner.dataset.slideNameColor : '#ffffff';
  }

  _updateSlideNameColor(slide, frame) {
    const color = this._getFrameSlideNameColor(frame);
    slide.querySelectorAll('.niccos-isstories-popup__slide-name').forEach((el) => {
      el.style.color = color;
    });
  }

  _initFrameEngine() {
    const slide = this._getActiveSlide();
    if (!slide) return;

    this._currentFrameIndex = 0;
    this._isPaused = false;
    this._barPausedElapsed = 0;

    this._updateVideoFlag(slide);
    this._showFrame(0);
  }

  _updateVideoFlag(slide) {
    const frames = this._getFrames(slide);
    const hasAnyVideo = frames.some((f) => this._getFrameType(f) === 'video');
    slide.classList.toggle('niccos-isstories-popup__slide--has-video', hasAnyVideo);
  }

  _showFrame(index) {
    const slide = this._getActiveSlide();
    if (!slide) return;

    const frames = this._getFrames(slide);
    const bars = this._getBars(slide);

    if (index < 0 || index >= frames.length) return;

    this._cancelBarAnimation();
    this._removeVideoEndedListener();
    this._stopAllVideos(slide);

    this._currentFrameIndex = index;
    this._isPaused = false;
    this._barPausedElapsed = 0;

    frames.forEach((f, i) => {
      f.classList.toggle('niccos-isstories-popup__frame--active', i === index);
    });

    bars.forEach((b, i) => {
      b.classList.remove('niccos-isstories-popup__bar--completed');
      const fill = b.querySelector('.niccos-isstories-popup__bar-fill');
      if (fill) {
        fill.style.width = '';
        fill.style.transform = 'scaleX(0)';
      }

      if (i < index) {
        b.classList.add('niccos-isstories-popup__bar--completed');
        if (fill) fill.style.transform = '';
      }
    });

    const currentFrame = frames[index];
    this._updateSlideNameColor(slide, currentFrame);
    const frameType = this._getFrameType(currentFrame);
    const playBtn = slide.querySelector('.niccos-isstories-popup__btn-play');
    if (playBtn) {
      playBtn.dataset.state = 'playing';
      playBtn.setAttribute('aria-label', 'Pause');
    }

    const hasVideo = frameType === 'video';
    slide.classList.toggle('niccos-isstories-popup__slide--has-video', hasVideo);

    if (hasVideo) {
      const video = this._getFrameVideo(currentFrame);
      if (video) {
        video.muted = this._isMuted;
        video.currentTime = 0;
        this._startVideoWhenReady(video, slide, currentFrame);
      } else {
        this._startBarAnimation(this._frameDuration * 1000);
      }
    } else {
      this._startBarAnimation(this._frameDuration * 1000);
    }

    this._syncMuteButton(slide);
  }

  _startVideoWhenReady(video, slide, frame) {
    this._cancelVideoReadyListener();

    const start = () => {
      this._cancelVideoReadyListener();
      frame.classList.remove('niccos-isstories-popup__frame--loading');

      if (this._videoUseOwnDuration) {
        const dur = video.duration;
        this._startBarAnimation(dur && isFinite(dur) ? dur * 1000 : this._frameDuration * 1000);
        this._addVideoEndedListener(video);
      } else {
        this._startBarAnimation(this._frameDuration * 1000);
      }
    };

    if (video.readyState >= 3) {
      const playPromise = video.play();
      if (playPromise) playPromise.catch(() => {});
      start();
    } else {
      frame.classList.add('niccos-isstories-popup__frame--loading');
      this._videoReadyHandler = start;
      this._videoReadyEl = video;
      video.addEventListener('canplay', start, { once: true });
      video.addEventListener('playing', start, { once: true });
      const playPromise = video.play();
      if (playPromise) playPromise.catch(() => {});
    }
  }

  _cancelVideoReadyListener() {
    if (this._videoReadyEl && this._videoReadyHandler) {
      this._videoReadyEl.removeEventListener('canplay', this._videoReadyHandler);
      this._videoReadyEl.removeEventListener('playing', this._videoReadyHandler);
    }
    this._videoReadyHandler = null;
    this._videoReadyEl = null;
  }

  _addVideoEndedListener(video) {
    this._removeVideoEndedListener();
    this._videoEndedHandler = () => this._advanceFrame();
    video.addEventListener('ended', this._videoEndedHandler, { once: true });
    this._activeVideoEl = video;
  }

  _removeVideoEndedListener() {
    if (this._activeVideoEl && this._videoEndedHandler) {
      this._activeVideoEl.removeEventListener('ended', this._videoEndedHandler);
    }
    this._videoEndedHandler = null;
    this._activeVideoEl = null;
  }

  _stopAllVideos(slide) {
    if (!slide) return;
    slide.querySelectorAll('.niccos-isstories-popup__frame-video').forEach((v) => {
      v.pause();
      v.currentTime = 0;
    });
  }

  _startBarAnimation(durationMs) {
    this._cancelBarAnimation();

    if (!durationMs || !isFinite(durationMs) || durationMs <= 0) {
      durationMs = this._frameDuration * 1000;
    }

    const slide = this._getActiveSlide();
    if (!slide) return;

    const bars = this._getBars(slide);
    const bar = bars[this._currentFrameIndex];
    if (!bar) return;

    const fill = bar.querySelector('.niccos-isstories-popup__bar-fill');
    if (!fill) return;

    this._barDuration = durationMs;
    const startProgress = Math.min(this._barPausedElapsed / durationMs, 1);
    fill.style.width = '';
    fill.style.transform = `scaleX(${startProgress})`;
    this._barStartTime = performance.now() - this._barPausedElapsed;

    const self = this;
    const animate = (now) => {
      const elapsed = now - self._barStartTime;
      const progress = Math.min(elapsed / self._barDuration, 1);
      fill.style.transform = `scaleX(${progress})`;

      if (progress >= 1) {
        self._barAnimationId = null;
        self._advanceFrame();
        return;
      }
      self._barAnimationId = requestAnimationFrame(animate);
    };

    this._barAnimationId = requestAnimationFrame(animate);
  }

  _cancelBarAnimation() {
    if (this._barAnimationId) {
      cancelAnimationFrame(this._barAnimationId);
      this._barAnimationId = null;
    }
    this._cancelVideoReadyListener();
  }

  _advanceFrame() {
    const slide = this._getActiveSlide();
    if (!slide) return;

    const frames = this._getFrames(slide);
    const nextIdx = this._currentFrameIndex + 1;

    if (nextIdx < frames.length) {
      this._showFrame(nextIdx);
    } else {
      this.nextStory();
    }
  }

  _prevFrame() {
    if (this._currentFrameIndex > 0) {
      this._showFrame(this._currentFrameIndex - 1);
    } else {
      if (this._currentIndex > 0) {
        this.prevStory();
      }
    }
  }

  _nextFrame() {
    const slide = this._getActiveSlide();
    if (!slide) return;

    const frames = this._getFrames(slide);
    const nextIdx = this._currentFrameIndex + 1;

    if (nextIdx < frames.length) {
      this._showFrame(nextIdx);
    } else {
      this.nextStory();
    }
  }

  _stopFrameEngine() {
    this._cancelBarAnimation();
    this._removeVideoEndedListener();

    const slide = this._getActiveSlide();
    if (slide) {
      this._stopAllVideos(slide);

      const frames = this._getFrames(slide);
      frames.forEach((f) => f.classList.remove('niccos-isstories-popup__frame--active'));

      const bars = this._getBars(slide);
      bars.forEach((b) => {
        b.classList.remove('niccos-isstories-popup__bar--completed');
        const fill = b.querySelector('.niccos-isstories-popup__bar-fill');
        if (fill) {
          fill.style.width = '';
          fill.style.transform = 'scaleX(0)';
        }
      });
    }

    this._currentFrameIndex = 0;
    this._isPaused = false;
    this._holdPaused = false;
    this._barPausedElapsed = 0;
  }


  _pause() {
    this._isPaused = true;
    this._barPausedElapsed = Math.min(performance.now() - this._barStartTime, this._barDuration);
    this._cancelBarAnimation();
    this._removeVideoEndedListener();

    const slide = this._getActiveSlide();
    if (!slide) return;
    const frame = this._getFrames(slide)[this._currentFrameIndex];
    if (frame && this._getFrameType(frame) === 'video') {
      const video = this._getFrameVideo(frame);
      if (video) video.pause();
    }
  }

  _resume() {
    this._isPaused = false;

    const slide = this._getActiveSlide();
    if (!slide) return;
    const frame = this._getFrames(slide)[this._currentFrameIndex];
    if (!frame) return;

    if (this._getFrameType(frame) === 'video') {
      const video = this._getFrameVideo(frame);
      if (video) {
        const p = video.play(); if (p) p.catch(() => {});
        if (this._videoUseOwnDuration && video.duration && isFinite(video.duration)) {
          this._barPausedElapsed = Math.round(video.currentTime * 1000);
          this._startBarAnimation(video.duration * 1000);
          this._addVideoEndedListener(video);
          return;
        }
      }
    }
    this._startBarAnimation(this._frameDuration * 1000);
  }

  _onPlayPauseClick(slide) {
    const playBtn = slide.querySelector('.niccos-isstories-popup__btn-play');

    if (this._isPaused) {
      this._resume();
      if (playBtn) { playBtn.dataset.state = 'playing'; playBtn.setAttribute('aria-label', 'Pause'); }
    } else {
      this._pause();
      if (playBtn) { playBtn.dataset.state = 'paused'; playBtn.setAttribute('aria-label', 'Play'); }
    }
  }


  _onMuteClick(slide) {
    this._isMuted = !this._isMuted;
    this._syncMuteButton(slide);
    this._applyMuteToActiveVideo(slide);
  }

  _syncMuteButton(slide) {
    const muteBtn = slide.querySelector('.niccos-isstories-popup__btn-mute');
    if (!muteBtn) return;
    muteBtn.dataset.state = this._isMuted ? 'muted' : 'unmuted';
    muteBtn.setAttribute('aria-label', this._isMuted ? 'Unmute' : 'Mute');
  }

  _applyMuteToActiveVideo(slide) {
    const frames = this._getFrames(slide);
    const currentFrame = frames[this._currentFrameIndex];
    if (!currentFrame) return;

    const video = this._getFrameVideo(currentFrame);
    if (video) video.muted = this._isMuted;
  }

  /* ── Keyboard ── */

  _onKeyDown(e) {
    if (e.key === 'Escape') this.close();
    if (e.key === 'ArrowLeft') this._prevFrame();
    if (e.key === 'ArrowRight') this._nextFrame();
  }

  _isPopupContentClick(target) {
    return (
      target.closest('.niccos-isstories-popup__slider') ||
      target.closest('.niccos-isstories-popup__arrow') ||
      target.closest('[data-niccos-isstories-close]')
    );
  }


  _onTouchStart(e) {
    this._touchStartX = e.touches[0].clientX;
    this._touchDeltaX = 0;
  }

  _onTouchMove(e) {
    this._touchDeltaX = e.touches[0].clientX - this._touchStartX;
  }

  _onTouchEnd() {
    const threshold = 50;
    if (this._touchDeltaX > threshold) {
      this.prevStory();
    } else if (this._touchDeltaX < -threshold) {
      this.nextStory();
    }
    this._touchDeltaX = 0;
  }
}

if (!customElements.get('niccos-isstories-popup')) {
  customElements.define('niccos-isstories-popup', NiccosIsstoriesPopup);
}
